/**
 * Tipi delle letture trasversali sui set di ADR 0030 §6: consumo per giorno,
 * cronologia unica e consumo per utente dell'amministratore. I client stanno
 * in `directory.ts` e `agents.ts`.
 */

/** Consumo di un giorno UTC. */
export interface DailyUsageDay {
  /** Giorno, `AAAA-MM-GG`. */
  date: string;
  /** Token conteggiati del giorno. */
  tokens: number;
  /** Token per id di set; solo i set con consumo. */
  by_set: Record<string, number>;
  /**
   * Con un set e `byProject` (ADR 0032): token per id di progetto, `all` per i
   * messaggi scritti per tutti i progetti; `null` o assente altrimenti.
   */
  by_project?: Record<string, number> | null;
}

/** `GET /api/v1/agent-sets/usage/daily`. */
export interface DailyUsage {
  /** Un elemento per giorno, dal più vecchio a oggi. */
  days: DailyUsageDay[];
  /** Somma dei giorni. */
  total: number;
  /** Giorno di picco; `null` senza consumo. */
  peak_day: string | null;
  /** Prezzo indicativo di un milione di token in dollari; `null` se non configurato. */
  price_per_million_usd: number | null;
}

/** Tipo di una voce della cronologia. */
export type HistoryKind = 'request' | 'response' | 'action' | 'voice';

/** Tipi della cronologia, nell'ordine dei filtri. */
export const HISTORY_KINDS: readonly HistoryKind[] = ['request', 'response', 'action', 'voice'];

/** Stati che una voce della cronologia può avere, nell'ordine dei filtri. */
export const HISTORY_STATUSES = ['queued', 'running', 'done', 'failed', 'cancelled', 'active', 'ended'] as const;

/** Stato di una voce della cronologia. */
export type HistoryStatus = (typeof HISTORY_STATUSES)[number];

/** Una voce della cronologia unica. */
export interface HistoryItem {
  /** Identificativo (del messaggio, della risposta, dell'evento o della sessione vocale). */
  id: string;
  /** Set. */
  set_id: string;
  /** Nome del set. */
  set_name: string;
  /** Progetto, se la voce ne ha uno. */
  project_id: string | null;
  /** Nome del progetto. */
  project_name: string | null;
  /** Tipo. */
  kind: HistoryKind;
  /** Testo, al più 300 caratteri. */
  text: string;
  /** Stato. */
  status: string;
  /** Chi ha agito. */
  actor_label: string;
  /** Istante della voce, ISO 8601. */
  created_at: string;
  /** Chiusura, ISO 8601; `null` per le azioni. */
  completed_at: string | null;
}

/** `GET /api/v1/agent-sets/messages`. */
export interface HistoryPage {
  /** Voci della pagina, dalla più recente. */
  items: HistoryItem[];
  /** Voci che rispettano i filtri. */
  total: number;
  /** Offset della pagina successiva; `null` all'ultima. */
  next_offset: number | null;
}

/** Filtri della cronologia (gli stessi per l'esportazione). */
export interface HistoryFilters {
  /** Solo questo set. */
  setId?: string;
  /** Solo questo progetto. */
  projectId?: string;
  /** Solo questo tipo. */
  kind?: HistoryKind;
  /** Solo questo stato. */
  status?: string;
  /** Testo cercato. */
  q?: string;
  /** Da questo istante (incluso), ISO 8601. */
  from?: string;
  /** Fino a questo istante (escluso), ISO 8601. */
  to?: string;
  /** Voci della pagina, al più 50. */
  limit?: number;
  /** Voci da saltare. */
  offset?: number;
}

/** Un utente nella pagina Utenti dell'amministratore: set e token del mese. */
export interface UserUsageItem {
  /** Utente. */
  subject_id: string;
  /** Set non archiviati posseduti. */
  owned_sets: number;
  /** Token conteggiati nel mese. */
  used_tokens: number;
}

/** `GET /api/v1/agent-sets/usage/users`. */
export interface UsersUsage {
  /** Mese UTC, `AAAA-MM`. */
  month: string;
  /** Utenti con set o consumo; chi manca ha zero. */
  items: UserUsageItem[];
}

/**
 * Query string dei filtri della cronologia.
 *
 * @param filters Filtri.
 * @returns La query, senza `?`.
 */
export function historyQuery(filters: HistoryFilters): string {
  const params = new URLSearchParams();
  const text = filters.q?.trim();
  const pairs: [string, string | number | undefined][] = [
    ['set_id', filters.setId],
    ['project_id', filters.projectId],
    ['kind', filters.kind],
    ['status', filters.status],
    ['q', text === '' ? undefined : text],
    ['from', filters.from],
    ['to', filters.to],
    ['limit', filters.limit],
    ['offset', filters.offset],
  ];
  for (const [name, value] of pairs) {
    if (value !== undefined && value !== '') {
      params.set(name, String(value));
    }
  }
  return params.toString();
}
